/**
 * opportunities/editOpportunity/saveForm.js - the editor's Save: settle the
 * customer, check required fields and the first task, create (with the first
 * task) or save only what changed under the r50 guards, upload staged files,
 * then the Won PO form or close.
 * Moved verbatim out of EditOpportunityModal.jsx; the editor passes in the
 * values it reads.
 */
import { firstTaskErrors } from '../../crm/crmFormHelpers';
import { isWonStatus } from '../editOpportunityConstants';
import { isProjectRow } from '../../../data/projectStatus';
import { notifyDocsChanged } from '../../../hooks/useDocIndex';
import {
  isProjectForm,
  buildSavePayload,
  deriveQuoteMath,
  loadedValues,
  theirValues,
  applyTheirs,
  diffForSave,
  OPP_DERIVED,
  OPP_UNGUARDED,
  rawValue,
} from '../editOpportunityForm';
import { runGuardedSave } from '../ConflictDialog';
import { takeSaveError } from '../../../utils/saveError';
import { blank, REQUIRED_LABELS } from './editorBits';
import { NEEDS_REASON } from '../editOpportunityConstants';

export function makeSaveForm({
  f,
  customerField,
  isCreate,
  setMissing,
  setTab,
  setScrollTo,
  setSaveError,
  noun,
  firstTaskProps,
  firstTask,
  setTaskErrs,
  scope,
  offeredQuote,
  setF,
  createdId,
  opp,
  ask,
  createWithFirstTask,
  createProject,
  setCreatedId,
  snap,
  loaded,
  stagedDocs,
  base,
  setStagedDocs,
  rebase,
  saveProject,
  savedStatus,
  openedAsProject,
  setToast,
  onSaved,
  setWonSetup,
  asPage,
  onClose,
}) {
  const saveForm = async () => {
    // r49 fix F6: a customer name still being typed is settled first, by the
    // field's own rules (EditOpportunityFields CustomerSearch) - the click on
    // Save no longer blurs it, because the confirm that blur opened ate the
    // click. Declining to add an unknown name puts the old one back and stops.
    let form = f;
    const settling = customerField.current && customerField.current.flush();
    const typedCustomer = settling ? await settling : null;
    if (typedCustomer) {
      if (!typedCustomer.ok) return;
      form = { ...f, customer: typedCustomer.value };
    }
    // The required fields. A NEW record needs its Name; an old one without
    // a name (11 of them) still saves an edit - refusing it was Q5335's
    // "Update did nothing". Customer always, and an opportunity's Quote
    // Date: a row missing either is filtered out of every list (it looks
    // "lost" even though it saved). Each blank one is named, turns red, and
    // the first is scrolled into view.
    const blanks = {};
    if (isCreate && blank(form.name)) blanks.name = true;
    if (blank(form.customer)) blanks.customer = true;
    if (!isProjectForm(form) && blank(form.quoteDate)) blanks.quoteDate = true;
    // not won = say why, when it moves there (an old closed record with no note still saves)
    if (NEEDS_REASON.includes(form.status) && form.status !== savedStatus.current && blank(form.closeReason)) blanks.closeReason = true;
    const names = Object.keys(blanks).map((k) => REQUIRED_LABELS[k]);
    if (names.length) {
      setMissing(blanks);
      setTab('summary');
      setScrollTo(Object.keys(blanks)[0]);
      const hidden = blanks.customer || blanks.quoteDate;
      setSaveError(
        `Please fill in: ${names.join(', ')}.${hidden ? ` Without ${names.length > 1 ? 'them' : 'it'} the ${noun.toLowerCase()} won't show in the list.` : ''}`
      );
      return;
    }
    setMissing({});
    // Someone types it by hand, so say what is wrong here rather than let the
    // server refuse the whole save.
    const prob = String(form.probability ?? '').trim();
    if (prob !== '' && !(/^\d{1,3}$/.test(prob) && Number(prob) <= 100)) {
      setSaveError('% Prob must be a whole number from 0 to 100.');
      return;
    }
    // T-03: no new opportunity without its first task
    if (firstTaskProps) {
      const te = firstTaskErrors(firstTask);
      if (Object.keys(te).length) {
        setTaskErrs(te);
        setTab('summary');
        setSaveError(Object.values(te)[0]);
        return;
      }
    }
    setSaveError('');
    // r50: "Use 5392" (the quote was just taken) saves again with that number
    const formFor = (o) => (o.quoteLetter ? { ...form, quoteLetter: o.quoteLetter } : form);
    const payloadOf = (fo) => buildSavePayload(fo, deriveQuoteMath(fo), { linkSite: !scope, ledger: !scope });
    const onQuote = (q) => {
      offeredQuote.current = q;
      setF((p) => ({ ...p, quoteLetter: q }));
    };
    let savedForm = form; // the form the last send was built from
    let payload = payloadOf(form);
    let recId = createdId || (opp && opp.id);
    let uploadError = ''; // the record saved, but some staged files did not upload
    if (isCreate && !createdId) {
      // A new record sends everything. This used to report "saved" whatever
      // happened; a refused save lost the whole form without a word.
      const res = await runGuardedSave({
        ask,
        onQuote,
        send: (o) => {
          savedForm = formFor(o);
          payload = payloadOf(savedForm);
          return firstTaskProps
            ? createWithFirstTask(payload, o)
            : createProject(payload, { allowDuplicateQuote: !!o.allowDuplicateQuote });
        },
      });
      if (!res) return; // stopped at a dialog: back to the form
      const created = res.ok ? res.body : null;
      if (!created || created.id == null) {
        setSaveError((res.body && res.body.error) || 'Could not save - try again.');
        return;
      }
      setCreatedId(created.id);
      recId = created.id;
      // a later Save (a file failed to upload) updates this record from here
      snap.current = payload;
      loaded.current = { ...payload, ...loadedValues(created, Object.keys(payload)) };
      // Push anything staged in the Doc Hub now that there is a row to own it.
      // Failures are reported rather than swallowed: the record saved, so
      // silently losing its RFQ would be the worst of both.
      if (created && created.id && stagedDocs.length) {
        const failed = [];
        for (const sd of stagedDocs) {
          const fd = new FormData();
          fd.append('file', sd.file);
          fd.append('doc_type', sd.doc_type || 'general');
          fd.append('doc_ref', sd.doc_ref || '');
          if (sd.folder) fd.append('folder', sd.folder);
          try {
            const r = await fetch(`${base}/projects/${encodeURIComponent(created.id)}/attachments`, { method: 'POST', body: fd });
            if (!r.ok) failed.push(sd.file.name);
          } catch {
            failed.push(sd.file.name);
          }
        }
        setStagedDocs([]);
        // the new Quote # link in the table waits on the doc index otherwise
        if (failed.length < stagedDocs.length) notifyDocsChanged();
        if (failed.length) {
          uploadError = `Saved, but these files did not upload: ${failed.join(', ')}. Open the ${noun.toLowerCase()} and add them again.`;
        }
      }
    } else {
      // r50: only what was edited here, each expecting the value it had when
      // the editor opened; nothing changed is nothing to send. The profit,
      // margin and anticipated value are never sent: the server works them
      // out from its own row and what is sent (OPP_DERIVED). The leftover is
      // sent when the PO value or the invoiced amount moves it, unguarded.
      let sent = {};
      const res = await runGuardedSave({
        ask,
        onQuote,
        onTheirs: (body) => {
          const theirs = theirValues(body);
          setF((p) => applyTheirs(p, theirs));
          rebase(applyTheirs(form, theirs), theirs);
        },
        send: (o) => {
          savedForm = formFor(o);
          payload = payloadOf(savedForm);
          const { changes, expect } = diffForSave(payload, snap.current, loaded.current, {
            derived: OPP_DERIVED,
            unguarded: OPP_UNGUARDED,
          });
          sent = changes;
          if (!Object.keys(changes).length) return Promise.resolve({ ok: true, status: 200, body: null });
          return saveProject(recId, o.allowDuplicateQuote ? { ...changes, allowDuplicateQuote: true } : changes, {
            expect,
            force: !!o.force,
          });
        },
      });
      if (!res) return;
      if (!res.ok) {
        setSaveError((res.body && res.body.error) || takeSaveError() || 'Could not save - try again.');
        return;
      }
      rebase(
        savedForm,
        Object.fromEntries(
          Object.keys(sent).map((k) => {
            const v = rawValue(res.body, k);
            return [k, v === undefined ? sent[k] : v];
          })
        )
      );
    }
    // Q-02: the Status dropdown (or a new record) made an opportunity Won -
    // ask for the PO. Worked out before the upload error stops the save, so a
    // record saved as Won is asked for its PO even when a file failed (the
    // form says which). A project (Won / Complete already, or a new one) has
    // its PO fields on its own Summary and is not asked.
    const nowWon = isWonStatus(form.status) && !isProjectRow({ status: savedStatus.current }) && !(isCreate && openedAsProject);
    savedStatus.current = form.status;
    if (uploadError && !(nowWon && recId)) {
      // the editor stays open with the error so the files can be added again
      setSaveError(uploadError);
      return;
    }
    if (!uploadError) setToast(`${isProjectForm(form) ? 'Project' : 'Opportunity'} saved`);
    if (onSaved) onSaved();
    if (nowWon && recId) {
      setWonSetup({ ...opp, ...payload, id: recId, uploadError });
      return;
    }
    // a project's page stays open on what was just saved; a pop-up closes
    if (asPage && !isCreate) return;
    setTimeout(onClose, 800);
  };
  return saveForm;
}
